
import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useBills } from "./hooks/useBills";
import { formatPrice, getStatusColor } from "./utils/formatters";
import { SearchBar } from "./SearchBar";
import { StatusFilter } from "./StatusFilter";
import { useTaxRate } from "@/hooks/useTaxRate";
import { FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InvoiceDialog } from "@/components/admin/bills/InvoiceDialog";
import { Bill } from "./types";

interface BillsTableProps {
  schoolId?: string;
  isPublicView?: boolean;
}

export function BillsTable({ schoolId, isPublicView = false }: BillsTableProps) {
  const {
    bills,
    loading,
    fetchBills,
    fetchBillsForSchool,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    resetFetchState
  } = useBills();
  
  const { taxRate } = useTaxRate();
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    // Only fetch if we haven't fetched before
    if (hasFetched) return;
    
    // Reset fetch state when component mounts
    resetFetchState();
    
    if (isPublicView && schoolId) {
      console.log('Fetching bills for school in BillsTable:', schoolId);
      fetchBillsForSchool(schoolId).then(() => setHasFetched(true));
    } else {
      fetchBills().then(() => setHasFetched(true));
    }
    
    // No dependencies for this effect - it should only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewInvoice = (bill: Bill) => {
    // Convert Bill to AdminBill format for the InvoiceDialog
    const adminBill = {
      ...bill,
      client: { nama_sekolah: "Your School" }, // This will be replaced by the actual school name
      created_at: new Date().toISOString(),
      subscription_id: bill.subscription_id || "",
      user_id: "",
    };
    
    setSelectedBill(adminBill as any);
    setIsInvoiceDialogOpen(true);
  };

  const formatDiscountDisplay = (bill: Bill) => {
    if (!bill.discount || bill.discount <= 0) return formatPrice(0);
    
    let percentageText = '';
    
    // Use the correct percentage value if available
    if (bill.discount_type === 'percentage') {
      // If discount_percentage exists but seems to be the actual amount instead of percentage
      // (this is the edge case we're fixing)
      if (typeof bill.discount_percentage === 'number') {
        if (bill.discount_percentage > 100 && bill.plan?.price) {
          // This is likely an error - the discount_percentage contains the amount
          // So calculate the actual percentage
          const calculatedPercentage = Math.round((bill.discount / bill.plan.price) * 100);
          percentageText = ` (${calculatedPercentage}%)`;
        } else {
          // Normal case - use the provided percentage
          percentageText = ` (${bill.discount_percentage}%)`;
        }
      } else if (bill.plan?.price) {
        // Fallback if no discount_percentage provided
        const calculatedPercentage = Math.round((bill.discount / bill.plan.price) * 100);
        percentageText = ` (${calculatedPercentage}%)`;
      }
    } else if (bill.plan?.price) {
      // For fixed discounts, calculate percentage
      const originalPrice = bill.plan.price;
      if (originalPrice > 0) {
        const calculatedPercentage = Math.round((bill.discount / originalPrice) * 100);
        percentageText = ` (${calculatedPercentage}%)`;
      }
    }
    
    return `${formatPrice(bill.discount)}${percentageText}`;
  };

  return (
    <div className="p-5 bg-white rounded-xl">
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <SearchBar value={searchTerm} onChange={setSearchTerm} />
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />
      </div>
      <div className="rounded-md overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Transaction #</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Plan Price</TableHead>
                <TableHead>Tax ({taxRate}%)</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Status</TableHead>
                {isPublicView && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {bills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isPublicView ? 11 : 10} className="text-center">
                    No bills found
                  </TableCell>
                </TableRow>
              ) : (
                bills.map((bill) => {
                  // Ensure plan and product properties exist to avoid undefined errors
                  const productName = bill.plan?.product?.name || 'Unknown Product';
                  const planName = bill.plan?.name || 'Unknown Plan';
                  const planPrice = bill.plan?.price || 0;
                  
                  return (
                    <TableRow key={bill.id}>
                      <TableCell>{bill.transaction_number}</TableCell>
                      <TableCell>{productName}</TableCell>
                      <TableCell>{planName}</TableCell>
                      <TableCell>{bill.description || '-'}</TableCell>
                      <TableCell>{formatPrice(planPrice)}</TableCell>
                      <TableCell>{formatPrice(bill.tax)}</TableCell>
                      <TableCell>{formatDiscountDisplay(bill)}</TableCell>
                      <TableCell className="font-medium">
                        {formatPrice(bill.amount)}
                      </TableCell>
                      <TableCell>
                        {new Date(bill.due_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(bill.status)}>
                          {bill.status.toUpperCase()}
                        </Badge>
                      </TableCell>
                      {isPublicView && (
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleViewInvoice(bill)}
                            className="flex items-center gap-1"
                          >
                            <FileText className="h-4 w-4" />
                            Invoice
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {selectedBill && (
        <InvoiceDialog
          bill={selectedBill as any}
          open={isInvoiceDialogOpen}
          onOpenChange={setIsInvoiceDialogOpen}
        />
      )}
    </div>
  );
}
